import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from "vue";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import ReleaseLotsModal from "@/components/ui/modals/ReleaseLotsModal.vue";
  import CreateOptionModal from "@/components/ui/modals/CreateOptionModal.vue";
  import TransitionModal from "@/components/ui/modals/TransitionModal.vue";
  import SetRegistrationDateModal from "@/components/ui/modals/SetRegistrationDateModal.vue";
  import StageMap from "@/components/StageMap.vue";
  import {
    statusFilters,
    StatusFiltersLabels,
    StatusFiltersValues,
  } from "@/constants/statusFilters";
  import { getLotStatusClass } from "@/helpers/status";
  import { formatDate } from "@/helpers/date";
  import router from "@/router";
  import { LotInterface } from "@/types/LotInterface";
  import { useStore } from "vuex";
  import { StageInterface } from "@/types/StageInterface";
  import { LotStatus } from "@/enums/LotStatus";
  import plural from "@/features/plural";
  import { formatPrice } from "@/helpers/transformation/price";
  import { InitialHoldInterface } from "@/types/InitialHoldInterface";
  import { AxiosResponse } from "axios";
  import { Business } from "@/types/BusinessInterface";
  import { getBusinessNameFromLot } from "@/helpers/lot/getBusinessNameFromLot";
  import { EstateInterface } from "@/types/EstateInterface";
  import { POSITION } from "vue-toastification";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'Lots',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const viewType = ref<"list" | "map">("list");

  const filters = statusFilters;
  const activeFilter = ref(statusFilters[0]);

  const stage = ref<StageInterface | null>(null);
  const lots = ref<LotInterface[]>([]);
  const released = ref(false);
  const showReleaseModal = ref(false);
  const showRegDateModal = ref(false);
  const showCreateOptionModal = ref(false);
  const showTransitionModal = ref(false);
  const estateId = computed(() => store.state.user.selectedEstateId);
  const userId = computed(() => store.state.user.data._id);
  const stageId = router.currentRoute.value.params.stageId;
  const lotList = ref([]);
  const businesses = ref<Business[]>([]);
  const isSelectAll = ref(false);
  const registrationDate = ref("");
  const attentionType = ref<"release" | "option" | "setDate" | "">("");
  const initialHolds = ref<InitialHoldInterface[] | null>(null);
  const activeLoaders = ref(0);
  const isLoading = computed(() => activeLoaders.value > 0);
  const estate = ref<EstateInterface | null>(null);

  const extraColumns = reactive({
    purchaser: {
      value: "Assigned to",
      isSelected: false,
    },
    exchanged: {
      value: "Exchanged",
      isSelected: false,
    },
    financeDue: {
      value: "Finance due",
      isSelected: false,
    },
    settlement: {
      value: "Settlement",
      isSelected: false,
    },
  });
  const pinnedLotColumns = computed(
    () => store.state.user.data.detailsId.pageSettings?.lotsTab?.showColumns
  );
  const estateHasCollaborators = computed(() => {
    const collaboratorsLength = estate.value?.collaboratorBusiness?.length;

    return collaboratorsLength && collaboratorsLength > 0;
  });
  const getEstate = () => {
    activeLoaders.value++;
    store
      .dispatch("estate/getEstate", estateId.value)
      .then((res) => {
        estate.value = res.data;
      })
      .catch((error) => {
        estate.value = null;
      })
      .finally(() => {
        activeLoaders.value--;
      });
  };

  onMounted(() => {
    updateUserProfile().then(() => {
      pinnedLotColumns.value.forEach((column: string) => {
        const objKey = Object.keys(extraColumns).find(
          (key) => extraColumns[key].value === column
        );

        if (objKey) {
          extraColumns[objKey].isSelected = true;
        }
      });
    });

    if (router.currentRoute.value.query.map) {
      viewType.value = "map";
    }

    store.dispatch("stage/getStage", stageId).then((res) => {
      stage.value = res.data;
      registrationDate.value = formatDate(stage.value.registrationDate);
    });
    getEstate();
    getLots();
    getBusinesses();
  });
  watch(
    () => estateId.value,
    () => {
      router.push({ name: "Stages" });
    }
  );

  function getLots() {
    activeLoaders.value++;
    store
      .dispatch("lot/getLots", {
        stageId,
        status:
          activeFilter.value.value === StatusFiltersValues.ANY
            ? ""
            : activeFilter.value.label,
        eoi: true,
      })
      .then((res) => {
        lots.value = res.data;
        lotList.value = lots.value.map((item) => {
          return {
            id: item._id,
            selected: false,
            status: item.status,
          };
        });
        getInitialHolds();
      })
      .finally(() => {
        activeLoaders.value--;
      });
  }

  const getBusinesses = () => {
    activeLoaders.value++;

    store
      .dispatch("business/getAllBusiness")
      .then((res: AxiosResponse<Business[]>) => {
        businesses.value = res.data;
      })
      .finally(() => {
        activeLoaders.value--;
      });
  };

  const getInitialHolds = () => {
    const lotIds = lotList.value.map(
      (lot: { id: string; selected: boolean; status: string }) => lot.id
    );

    activeLoaders.value++;
    store
      .dispatch("initialHold/getApprovedInitialHoldsByLotsIds", {
        lotIds,
        estateId: estateId.value,
      })
      .then((res) => {
        initialHolds.value = res.data;
      })
      .finally(() => {
        activeLoaders.value--;
      });
  };

  function changeView() {
    viewType.value = viewType.value === "list" ? "map" : "list";
  }
  function changeFilter(item: {
    label: StatusFiltersLabels;
    value: StatusFiltersValues;
  }) {
    activeFilter.value = item;
    getLots();
  }
  function toLot(id: string) {
    router.push({
      name: "LotActions",
      params: { stageId, lotId: id },
    });
  }

  function selectLot(evnt: boolean, index: number) {
    lotList.value[index].selected = !lotList.value[index].selected;
    isSelectAll.value = selectedLotsList.value.length > 0;
  }

  function selectAll(isSelect: boolean) {
    isSelectAll.value = isSelect;
    lotList.value.forEach((item) => {
      item.selected = isSelect;
    });
  }

  const onUpdateRegistrationDate = () => {
    isSelectAll.value = false;
  };
  const selectedLotsList = computed(() => {
    return lotList.value.filter((item) => item.selected);
  });

  const selectedForReleaseList = computed(() => {
    return lotList.value.filter(
      (item) => item.selected && item.status === LotStatus.NOT_RELEASED
    );
  });

  const selectedForOptionList = computed(() => {
    return lotList.value.filter(
      (item) =>
        item.selected &&
        (item.status === LotStatus.NOT_RELEASED ||
          item.status === LotStatus.AVAILABLE)
    );
  });

  const showRelease = computed(() => {
    if (selectedLotsList.value.length == 0) {
      return false;
    } else {
      if (!selectedForReleaseList.value.length) {
        return false;
      }
    }

    return true;
  });

  const showCreateOption = computed(() => {
    if (selectedLotsList.value.length == 0) {
      return false;
    } else {
      if (!selectedForOptionList.value.length) {
        return false;
      }
    }

    return true;
  });

  async function clickReleaseLot() {
    if (selectedLotsList.value.length != selectedForReleaseList.value.length) {
      attentionType.value = "release";
      showTransitionModal.value = true;
    } else {
      showReleaseModal.value = true;
    }
  }

  const clickAction = (type: string) => {
    if (type === "option") {
      if (
        selectedLotsList.value.length ===
        selectedLotsList.value.filter(
          (item) =>
            item.status === LotStatus.AVAILABLE ||
            item.status === LotStatus.NOT_RELEASED
        ).length
      ) {
        showCreateOptionModal.value = true;
      } else {
        showTransitionModal.value = true;
        attentionType.value = type;
      }
    } else {
      showRegDateModal.value = true;
    }
  };

  const closeReleaseModal = (update) => {
    if (update) {
      getLots();
      isSelectAll.value = false;
    }

    showReleaseModal.value = false;
  };

  const closeCreateOptionModal = (update) => {
    if (update) {
      getLots();
      isSelectAll.value = false;
    }

    showCreateOptionModal.value = false;
    attentionType.value = "";
  };

  const closeRegDateModal = (update) => {
    if (update) {
      getLots();
      isSelectAll.value = false;
    }

    showRegDateModal.value = false;
  };

  function proceed() {
    showTransitionModal.value = false;

    switch (attentionType.value) {
      case "release":
        showReleaseModal.value = true;
        break;
      case "option":
        showCreateOptionModal.value = true;
        break;
      case "setDate":
        showRegDateModal.value = true;
        break;
    }
  }

  const showAllColumns = (isSelected: boolean) => {
    for (const item in extraColumns) {
      extraColumns[item].isSelected = isSelected;
    }

    saveSelectedLotExtraColumns();
  };

  const getPurchaser = (lot: LotInterface): string => {
    const { status, offlineSale, applicants, _id } = lot;
    const { offlineBuyerData } = offlineSale?.offlineBuyerData ?? {};
    const { firstName, lastName } = applicants[0] ?? {};
    const { OFFLINE, AVAILABLE, HOLD } = LotStatus;

    if (status === AVAILABLE) {
      return "--";
    }

    if (status === OFFLINE && offlineBuyerData?.fullName) {
      return offlineBuyerData.fullName;
    }

    if (lot.eoiId?.buyersData?.firstBuyer) {
      return `${lot.eoiId.buyersData.firstBuyer.firstName} ${lot.eoiId.buyersData.firstBuyer.lastName}`;
    }

    if (status === HOLD && initialHolds.value?.length) {
      return getPurchaserLotOnHold(_id);
    }

    if (applicants.length) {
      return `${firstName} ${lastName}`;
    }

    return "--";
  };
  const getPurchaserLotOnHold = (lotId: string) => {
    const hold = initialHolds.value?.find((item) => item.lotId === lotId);

    if (!hold) {
      return "--";
    }

    return `${hold.leadsDetails.firstName} ${hold.leadsDetails.lastName}`;
  };
  const updateUserProfile = () => {
    return store.dispatch("user/updateUserProfile", userId.value);
  };

  const saveSelectedLotExtraColumns = () => {
    const lotColumns = [];

    for (const item in extraColumns) {
      if (extraColumns[item].isSelected) {
        lotColumns.push(extraColumns[item].value);
      }
    }

    store.dispatch("user/saveSelectedLotExtraColumns", lotColumns);
  };

  function getDisplayStatusClass(status: LotStatus) {
    return getLotStatusClass(
      status === LotStatus.EOI ? LotStatus.PRE_CONTRACT : status
    );
  }

const __returned__ = { store, viewType, filters, activeFilter, stage, lots, released, showReleaseModal, showRegDateModal, showCreateOptionModal, showTransitionModal, estateId, userId, stageId, lotList, businesses, isSelectAll, registrationDate, attentionType, initialHolds, activeLoaders, isLoading, estate, extraColumns, pinnedLotColumns, estateHasCollaborators, getEstate, getLots, getBusinesses, getInitialHolds, changeView, changeFilter, toLot, selectLot, selectAll, onUpdateRegistrationDate, selectedLotsList, selectedForReleaseList, selectedForOptionList, showRelease, showCreateOption, clickReleaseLot, clickAction, closeReleaseModal, closeCreateOptionModal, closeRegDateModal, proceed, showAllColumns, getPurchaser, getPurchaserLotOnHold, updateUserProfile, saveSelectedLotExtraColumns, getDisplayStatusClass, computed, onMounted, reactive, ref, watch, DropDown, CheckboxInput, ReleaseLotsModal, CreateOptionModal, TransitionModal, SetRegistrationDateModal, StageMap, get statusFilters() { return statusFilters }, get StatusFiltersLabels() { return StatusFiltersLabels }, get StatusFiltersValues() { return StatusFiltersValues }, get getLotStatusClass() { return getLotStatusClass }, get formatDate() { return formatDate }, get router() { return router }, get LotInterface() { return LotInterface }, get useStore() { return useStore }, get StageInterface() { return StageInterface }, get LotStatus() { return LotStatus }, get plural() { return plural }, get formatPrice() { return formatPrice }, get InitialHoldInterface() { return InitialHoldInterface }, get AxiosResponse() { return AxiosResponse }, get Business() { return Business }, get getBusinessNameFromLot() { return getBusinessNameFromLot }, get EstateInterface() { return EstateInterface }, get POSITION() { return POSITION } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})